import {
  type NodeId,
  type NodeSn,
  type NodeType,
  type TableRequestBody,
  isNonNullable,
  type settingTypes,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { DeviceView } from 'components/DeviceView/DeviceView';
import type { TableCellContext } from 'components/Table/model/TableCellContext';
import type {
  TableBulkAction,
  TableBulkActionClickEvent,
} from 'components/Table/model/TableModel';
import { useLocale } from 'containers/App/LocaleContext';
import type { ConfirmDialogMessages } from 'containers/ConfirmDialog/ConfirmDialog';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { type FC, memo, useMemo } from 'react';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { ApiTable } from 'components/ApiTable/components/ApiTable';
import { useTableDeviceView } from 'components/ApiTablePage/useTableDeviceView';
import { RelatedDeviceList } from 'components/RelatedDeviceList/RelatedDeviceList';
import type { SnapshotCreationHook } from './useSnapshotCreation';

export const snapshotCreationApiEndpoint = '/tables/snapshot-devices';

export type TaskApiSource =
  (typeof settingTypes.vendorApiNames)[keyof typeof settingTypes.vendorApiNames];

type TaskSource =
  | 'aciEndpoint'
  | 'arp'
  | 'previouslyDiscovered'
  | 'routes'
  | 'scanner'
  | 'seed'
  | 'trace'
  | 'virtualContext'
  | 'xdp'
  | 'ndpv6'
  | TaskApiSource;

export type VendorsSettingsId = string;
export type TaskApiSourceMap = {
  [Source in TaskApiSource]: {
    id: VendorsSettingsId;
    label: string;
  }[];
};

export type TaskSettingsState =
  | 'excluded'
  | 'noApiSettings'
  | 'noCredentials'
  | 'notIncluded'
  | 'ok'
  | 'unreachableIp';

export interface SnapshotCreationRowData {
  family: string;
  hostname: string;
  id: NodeId;
  isApiTask: boolean;
  isSelected: boolean;
  model: string;
  parentId: NodeId;
  platform: string;
  settingsStates: TaskSettingsState[];
  siteName: string;
  sn: NodeSn;
  source: TaskSource;
  type: NodeType;
  vendor: string;
  version: string;
}

export interface SnapshotCreationTableRequestBody
  extends TableRequestBody<SnapshotCreationRowData> {
  bindVariables: {
    selected: NodeSn[];
    isDelete?: boolean /* default is false */;
  };
}

export interface SnapshotCreationTableProps {
  preselectedSns: NodeSn[];
  canCreateNewSnapshot: boolean;
  disableLinks?: boolean;
  snapshotCreation: SnapshotCreationHook;
}

const selectedNone = { selected: [] };

const BareSnapshotCreationTable: FC<SnapshotCreationTableProps> = (props) => {
  const msg = useLocale();
  const confirmDialog = useSimpleConfirmDialog();

  const endpoint = useTableEndpoint({
    apiEndpoint: '/tables/snapshot-devices',
  });

  // Run create, refresh or delete opreation including device dependencies (e.g. APs associated to selected WLC
  // and vice versa).
  const makeRunSnapshotOperationWithDependencies =
    (
      isDelete: boolean,
      confirmDialogMessages: ConfirmDialogMessages,
      runOperation: (selectedDeviceSns: NodeSn[]) => Promise<void>,
    ) =>
    async (deviceSns: NodeSn[]) => {
      const allDevicesToProcess = await props.snapshotCreation.loadDependencies(
        deviceSns,
        isDelete,
      );
      if (allDevicesToProcess.length > deviceSns.length) {
        if (
          !(await confirmDialog.show(confirmDialogMessages, {
            children: (
              <RelatedDeviceList relatedDevices={allDevicesToProcess} />
            ),
            btnConfirmAccent: isDelete ? 'negative' : 'positive',
            capToWindowHeight: true,
          }))
        ) {
          return false;
        }
      }
      await runOperation(allDevicesToProcess.map(prop('sn')));
      return true;
    };

  const deleteDevices = useStableCallback(
    makeRunSnapshotOperationWithDependencies(
      true,
      msg.snapshotCreation.confirmDeleteRelated,
      props.snapshotCreation.onDeleteDevices,
    ),
  );

  const refreshDevices = useStableCallback(
    makeRunSnapshotOperationWithDependencies(
      false,
      msg.snapshotCreation.confirmRefreshRelated,
      props.snapshotCreation.onRefreshDevices,
    ),
  );

  const createSnapshot = useStableCallback(
    makeRunSnapshotOperationWithDependencies(
      false,
      msg.snapshotCreation.confirmCreateRelated,
      props.snapshotCreation.onCreateNewSnapshot,
    ),
  );

  const bulkActions = useMemo(
    (): TableBulkAction[] =>
      (
        [
          props.canCreateNewSnapshot
            ? ({
                key: 'new',
                title: msg.snapshotCreation.bulkActions.newSnapshot,
                icon: 'fa-play-circle',
                disableAutoDeselect: true,
                onClick: async ({
                  selectedRows,
                  deselectAll,
                  indicateRunningAction,
                }: TableBulkActionClickEvent) =>
                  indicateRunningAction(async () => {
                    if (
                      await createSnapshot(
                        Array.from(selectedRows as Set<NodeSn>),
                      )
                    ) {
                      deselectAll();
                    }
                  }),
              } as const)
            : null,
          {
            key: 'refresh',
            title: msg.snapshotCreation.bulkActions.refreshDevices,
            icon: 'fa-arrows-rotate',
            disableAutoDeselect: true,
            onClick: async ({
              selectedRows,
              deselectAll,
              indicateRunningAction,
            }: TableBulkActionClickEvent) =>
              indicateRunningAction(async () => {
                if (
                  await refreshDevices(Array.from(selectedRows as Set<NodeSn>))
                ) {
                  deselectAll();
                }
              }),
          },
          {
            key: 'delete',
            title: msg.snapshotCreation.bulkActions.deleteDevices,
            accent: 'negative',
            icon: 'fa-trash-can',
            disableAutoDeselect: true,
            onClick: async ({
              selectedRows,
              deselectAll,
              indicateRunningAction,
            }: TableBulkActionClickEvent) =>
              indicateRunningAction(async () => {
                if (
                  await deleteDevices(Array.from(selectedRows as Set<NodeSn>))
                ) {
                  deselectAll();
                }
              }),
          },
        ] as const
      ).filter(isNonNullable),
    [props.canCreateNewSnapshot, msg],
  );

  const tableDeviceView = useTableDeviceView(endpoint);

  const tableCellContext: Partial<TableCellContext> = useMemo(
    () => ({
      ...tableDeviceView.tableCellContext,
      disableLinks: props.disableLinks,
    }),
    [tableDeviceView.tableCellContext, props.disableLinks],
  );

  const snapshotId = useSnapshotId();

  return (
    <>
      <ApiTable
        borderless
        bindVariables={selectedNone}
        bulkActions={bulkActions}
        className={classNames(
          'flex-1 -m',
          tableDeviceView.tableClassName,
          // This is meant to render the border on Discovery Snapshot page
          // but not on Graph Snapshot Creation.
          // It is a temporary solution until the table and these sections are
          // fully redesigned, so a documented workaround is preferred over drilling
          // another prop.
          !props.canCreateNewSnapshot && 'border-t border-default',
        )}
        endpoint={endpoint}
        initialSelectedRows={props.preselectedSns}
        rowIdField='sn'
        tableCellContext={tableCellContext}
      />
      {confirmDialog.render()}
      <DeviceView
        deviceView={tableDeviceView.deviceView}
        disableDetach
        panelClassName='relative z-popup'
        snapshotId={snapshotId}
      />
    </>
  );
};

export const SnapshotCreationTable = memo(BareSnapshotCreationTable);
